import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import Address from './Address';
import Project from './Project';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import {ActionService} from '../../api';
import Country from './Country';
import ProjectContract from './ProjectContract';

export default class ProjectPart extends BaseModel {
  constructor({
    id,
    number,
    designation,
    active = true,
    status = undefined,
    customer = undefined,
    project = undefined,
    projectDesignation = undefined,
    tags = [],
    planning = false,
    isQuotation = false,
    isWork = false,
    memo = undefined,
    header = undefined,
    description = undefined,
    footer = undefined,
    quotationDate = undefined,
    creationDate = undefined,
    validityDate = undefined,
    amount = 0,
    lockOverheadCosts = false,
    locked = false,
    // We also need to add the key how it is stored in the form (we use the db column names)
    no = undefined,
    street = undefined,
    houseNo = undefined,
    boxNo = undefined,
    postcode = undefined,
    city = undefined,
    country = undefined,
    location = undefined,
    projectContract = undefined,
    globalScriptParam = undefined,
  }) {
    super(id, number || no, designation);
    this.active = active;
    this.status = status
      ? HitOnlineEnums.getEnumValue('PROJECT_PART_STATUS', status, translate)
      : null;
    this.customer = customer ? new Address(customer) : undefined;
    this.project =
      project || projectDesignation
        ? new Project(project || projectDesignation)
        : undefined;
    this.tags = tags;
    this.planning = planning;
    this.isQuotation = isQuotation;
    this.isWork = isWork;
    this.memo = memo;
    this.header = header;
    this.description = description;
    this.footer = footer;
    this.quotationDate = quotationDate;
    this.validityDate = validityDate;
    this.creationDate = creationDate;
    this.amount = amount;
    this.lockOverheadCosts = lockOverheadCosts;
    this.locked = locked;
    this.street = street;
    this.houseNo = houseNo;
    this.boxNo = boxNo;
    this.postcode = postcode;
    this.city = city;
    this.country = country ? new Country(country) : null;
    this.location = location;
    this.projectContract = projectContract
      ? new ProjectContract(projectContract)
      : undefined;
    this.globalScriptParam = globalScriptParam;
  }

  static get baseModuleForRedirection() {
    return 'project-base';
  }
  get baseModuleForRedirection() {
    return ProjectPart.baseModuleForRedirection;
  }

  static get defaultSort() {
    return {
      property: 'number',
      order: 'asc',
    };
  }

  getDefaultSort() {
    return ProjectPart.defaultSort;
  }

  static get printTemplateType() {
    return 'projectPart';
  }
  get printTemplateType() {
    return ProjectPart.printTemplateType;
  }

  get prefixIcon() {
    return ProjectPart.getPrefixIcon(this);
  }

  /** Checks if there is a prefix icon that can be displayed for the entity */
  static getPrefixIcon(projectPart) {
    const status = projectPart?.status?.id;
    if (!status) return null;
    if (status.startsWith('QUOTATION')) return 'quotation';
    if (status.startsWith('WORK')) return 'work';
    return null;
  }

  static get config() {
    return {
      ...BaseModel.config,
      importable: false,
      addable: false,
      duplicatable: true,
    };
  }

  get config() {
    return {
      ...BaseModel.config,
      importable: false,
      addable: false,
      duplicatable: true,
    };
  }

  static get fkName() {
    return 'project_part_id';
  }
  static get apiRoute() {
    return 'project_part';
  }
  static get hasEntityStaffRolesBulkUpdate() {
    return true;
  }
  static get detailPageRoute() {
    return 'projectPartDetail';
  }
  static get listPageRoute() {
    return 'projectDetail';
  }
  static get joinFilterColumns() {
    return ['search'];
  }
  get fkName() {
    return ProjectPart.fkName;
  }
  get apiRoute() {
    return ProjectPart.apiRoute;
  }
  get listPageRoute() {
    return ProjectPart.listPageRoute;
  }
  get detailPageRoute() {
    return ProjectPart.detailPageRoute;
  }
  static get attrsToStoreInForm() {
    return ['number', 'designation', 'project'];
  }
  get attrsToStoreInForm() {
    return BaseModel.attrsToStoreInForm;
  }
  get joinFilterColumns() {
    return ProjectPart.joinFilterColumns;
  }

  get fastModeBrowserDesignationWithCustomer() {
    const standardDesignation = this.fullDesignation;
    if (!this.customer) return standardDesignation;
    const customerDesignation = this.customer.fullDesignation;
    return `${standardDesignation}  [${customerDesignation}]`;
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'project',
      'number',
      'designation',
      'active',
      'status',
      'customer',
      'amount',
      'tags',
      'creationDate',
      'modificationDate',
      'quotationDate',
      'validityDate',
    ]);
  }

  static get entityListDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'active',
      'status',
      'customer',
      'amount',
      'tags',
    ]);
  }

  static get joinAttrsKey() {
    return [
      'number',
      'designation',
      'customer',
      'project',
      'isQuotation',
      'isWork',
      'status',
    ];
  }
  get fullDesignation() {
    return HitUtils.createStandardDesignation(
      {
        project: HitUtils.createStandardDesignation(
          this.project,
          'number',
          'designation',
          '.',
        ),
        projectPart: HitUtils.createStandardDesignation(
          this,
          'number',
          'designation',
          '.',
        ),
      },
      'project',
      'projectPart',
    );
  }

  get entityDesignation() {
    return HitUtils.createStandardDesignation(this);
  }

  get createInitColumns() {
    return {
      designation: '',
      project_id: this.project.id,
    };
  }

  static get orderKey() {
    return 'project(number)';
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'project',
      'number',
      'designation',
      'customer',
      'active',
      'status',
      'tags',
      'creationDate',
      'projectContract',
    ]);
  }

  static get positionInformationAttrs() {
    return this.getAttributes(
      [],
      [
        'project',
        'number',
        'designation',
        'customer',
        'isWork',
        'lockOverheadCosts',
      ],
    );
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'customer',
        'active',
        'status',
        'tags',
      ]),
      ...this.entityStaffRolesBulkUpdateAttrs,
    };
  }
  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE(),
      this.DESIGNATION_ATTRIBUTE({}, {maxWidth: '1fr'}),
      this.ACTIVE_ATTRIBUTE(),
      new AT.EnumerationAttribute({
        name: 'status',
        enumType: 'PROJECT_PART_STATUS',
        enumClass: HitOnlineEnums,
        tableProperties: {
          propertyName: 'status',
          header: 'hit-app.common.status',
          maxWidth: '1.5fr',
        },
      }),
      new AT.FkAddressAttribute({
        name: 'customer',
        joinPath: ['address!fk_project_part_address_id_address'],
        tableProperties: {
          header: 'hit-app.common.customer',
          maxWidth: '1fr',
        },
      }),
      new AT.FkProjectAttribute({
        name: 'project',
        joinPath: ['project!fk_project_part_project_id_project!inner'],
        supplementaryJoinColumns: ['individualAddress'],
        tableProperties: {
          header: 'hit-base.common.project',
        },
      }),
      new AT.FkProjectAttribute({
        name: 'projectDesignation',
        joinPath: ['project!fk_project_part_project_id_project!inner'],
        onlyFetchDesignation: true,
      }),
      new AT.DecimalAttribute({
        name: 'amount',
        column: 'total_sales_amount',
        tableProperties: {
          header: 'hit-app.common.amount',
        },
        decimalPrecision: 2,
      }),
      this.TAGS_ATTRIBUTE({
        moduleUsage: 'project',
        tableOrigin: 'project_part',
      }),
      new AT.BooleanAttribute({
        name: 'planning',
        tableProperties: {
          header: 'hit-base.common.planning',
        },
      }),
      new AT.BooleanAttribute({
        name: 'isQuotation',
        column: 'is_quotation',
        tableProperties: {
          header: 'hit-app.project.quotation-part',
        },
      }),
      new AT.BooleanAttribute({
        name: 'isWork',
        column: 'is_work',
        tableProperties: {
          header: 'hit-app.project.work-part',
        },
      }),
      this.MEMO_RICH_TEXT_ATTRIBUTE(),
      this.HEADER_RICH_TEXT_ATTRIBUTE(),
      this.DESCRIPTION_RICH_TEXT_ATTRIBUTE(),
      this.FOOTER_RICH_TEXT_ATTRIBUTE(),
      new AT.DateAttribute({
        name: 'quotationDate',
        column: 'quotation_date',
        tableProperties: {
          header: 'hit-app.common.quotation-date',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DateAttribute({
        name: 'validityDate',
        column: 'validity_date',
        tableProperties: {
          header: 'hit-app.project-part.valid-until',
          maxWidth: '0.5fr',
        },
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.StringAttribute({
        name: 'customerReference',
        column: 'customer_reference',
        tableProperties: {
          header: 'hit-app.project-part.customer-reference',
          maxWidth: '1fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'lockOverheadCosts',
        column: 'lock_overhead',
        default: false,
        tableProperties: {
          header: 'hit-app.project.work-part',
        },
      }),
      new AT.BooleanAttribute({
        name: 'locked',
        column: 'locked',
      }),
      new AT.StringAttribute({
        name: 'street',
        column: 'street',
        tableProperties: {
          header: 'hit-app.common.street',
        },
      }),
      new AT.StringAttribute({
        name: 'houseNo',
        column: 'house_no',
        tableProperties: {
          header: 'hit-app.common.house-no',
        },
      }),
      new AT.StringAttribute({
        name: 'boxNo',
        column: 'box_no',
        tableProperties: {
          header: 'hit-app.common.box-number',
        },
      }),
      new AT.StringAttribute({
        name: 'postcode',
        column: 'postcode',
        tableProperties: {
          header: 'hit-app.common.postcode',
        },
      }),
      new AT.StringAttribute({
        name: 'city',
        column: 'city',
        tableProperties: {
          header: 'hit-app.common.city',
        },
      }),
      new AT.FkCountryAttribute({
        name: 'country',
        joinPath: ['country!fk_project_part_country_country'],
        tableProperties: {
          header: 'hit-base.common.country',
        },
      }),
      new AT.LocationAttribute({
        name: 'location',
        column: 'location',
        tableProperties: {
          header: 'hit-base.common.location',
        },
      }),
      new AT.FkProjectContractAttribute({
        name: 'projectContract',
        joinPath: [
          'project_contract!fk_project_part_project_contract_id_project_contract',
        ],
        tableProperties: {
          header: 'hit-app.project-contract.project-contract',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'globalScriptParam',
        column: 'global_script_param',
        default: {},
      }),
    ];
  }

  /**
   * Only join the necessary attributes for the designation
   */
  static get joinAttrsForDesignation() {
    const designationKeys = [
      'number',
      'designation',
      'projectDesignation',
      'status',
    ];
    return designationKeys
      .map((key) => this.allAttributes.find((attr) => attr.name === key))
      .filter((attr) => attr !== undefined);
  }

  redirectToList(router) {
    router.push({
      name: 'projectDetail',
      params: {
        id: this.project?.id ?? 0,
      },
    });
  }

  static recalculate(project_part_id, silent = false) {
    return ActionService.depositTask(
      'recalculate-project-part',
      {
        project_part_id: project_part_id,
      },
      silent,
    );
  }

  recalculate(silent = false) {
    ProjectPart.recalculate(this.id, silent);
  }

  async loadTaxCodePreferences() {
    /**
     * Loads the preferences config to set the tax codes in project parts
     * 1. VAT code linked to customer
     * 2. Resource vat code (handled API side in insert-resources handler)
     * 3. Default VAT code (config)
     */
    const customerTaxCode = this.customer?.salesInvoiceTaxCode;
    const defaultTaxCode = await Address.loadCompanyBillingDefault(
      'taxCode',
      'SALE',
      'INVOICE',
    );
    return {
      customer: customerTaxCode,
      default: defaultTaxCode ?? null,
    };
  }
}
